/* eslint-disable no-unused-vars */
import {
  call, put, select, delay,
} from 'redux-saga/effects';
import { getLocale, getRefreshToken, getToken } from './redux';
import { CONNECTION_ERROR, OK } from '../configs/serverStatusCodes';
import { rejectActionPromise } from './rejectActionPromise';
import { authorizedLogoutSuccessful, authorizedRefreshSuccessful } from '../modules/Authorized/actions';
import { intlShape } from '../components/base/IntlProviderWrapper';
import { shouldRefreshToken } from './authToken';
import { requestTokenRefresh } from '../modules/Authorized/sagas/authorizedRefresh';

const TOKEN_REFRESH_SKEW_SECONDS = Number(process.env.REACT_APP_TOKEN_REFRESH_SKEW_SECONDS || 90);

const shouldSkipRefresh = url => (
  url.includes('/auth/login')
  || url.includes('/auth/login-inspector')
  || url.includes('/auth/refresh')
  || url.includes('/auth/logout')
);

const createSyntheticJsonResponse = body => ({
  ...body,
  json: () => Promise.resolve(body),
});

function* refreshAccessToken(token, refreshToken, locale) {
  if (!token) {
    return null;
  }

  try {
    const response = yield call(requestTokenRefresh, token, locale, refreshToken);
    const body = yield response.json();
    if (response.status === OK && body?.data?.access) {
      yield put(authorizedRefreshSuccessful(body.data));
      return body.data;
    }
  } catch (e) {
    return null;
  }

  return null;
}

function* doRequest(url, method, token, pool, locale, headers, noStringify) {
  const {
    formatMessage,
  } = intlShape;
  const data = {
    method,
    cache: 'no-cache',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Accept-Language': locale,
      Authorization: `Bearer ${token}`,
    },
  };
  data.headers = { ...data.headers, ...headers };
  const urlWithQuery = new URL(url);
  if (pool) {
    if (method !== 'GET') {
      if (noStringify) {
        data.body = pool;
        delete data.headers['Content-Type'];
      } else {
        data.body = JSON.stringify(pool);
      }
    } else {
      Object.keys(pool).forEach(key => {
        if (key === 'filter' || key === 'sort') {
          pool[key].forEach(item => urlWithQuery.searchParams.append(key, item));
        } else if (Array.isArray(pool[key])) {
          pool[key].forEach(item => urlWithQuery.searchParams.append(`${key}[]`, item));
        } else {
          urlWithQuery.searchParams.append(key, pool[key]);
        }
      });
    }
  }
  let fetchData = null;
  try {
    fetchData = yield fetch(urlWithQuery, data);
  } catch (e) {
    fetchData = createSyntheticJsonResponse({ status: CONNECTION_ERROR, message: formatMessage({ id: 'error.connection' }) });
  }
  return fetchData;
}

export default function* makeRequest(url, pool = {}, action, method = 'POST', removeToken = false, headers = {}, noStringify = false) {
  const {
    formatMessage,
  } = intlShape;
  const token = yield select(getToken);
  const refreshToken = yield select(getRefreshToken);
  const locale = yield select(getLocale);
  try {
    let effectiveToken = token;
    let effectiveRefreshToken = refreshToken;
    const canRefresh = !shouldSkipRefresh(url);

    if (canRefresh && shouldRefreshToken(effectiveToken, TOKEN_REFRESH_SKEW_SECONDS)) {
      const refreshed = yield call(refreshAccessToken, effectiveToken, effectiveRefreshToken, locale);
      effectiveToken = refreshed?.access || effectiveToken;
      effectiveRefreshToken = refreshed?.refresh || effectiveRefreshToken;
    }

    let userDataResponse = yield doRequest(url, method, effectiveToken, pool, locale, headers, noStringify);

    if (canRefresh && userDataResponse.status === 401) {
      const refreshed = yield call(refreshAccessToken, effectiveToken, effectiveRefreshToken, locale);
      if (refreshed?.access) {
        userDataResponse = yield doRequest(url, method, refreshed.access, pool, locale, headers, noStringify);
      }
    }

    if (userDataResponse.status === 401 && canRefresh) {
      yield put(authorizedLogoutSuccessful());
      window.location.href = '/';
    }
    return userDataResponse;
  } catch (e) {
    const userDataResponse = createSyntheticJsonResponse({ status: CONNECTION_ERROR, message: formatMessage({ id: 'error.connection' }) });
    yield call(rejectActionPromise(action, userDataResponse));
    return userDataResponse;
  }
}

export function* getBodyJson(res, defaultValue = null) {
  let data;
  if (!res || typeof res.json !== 'function') {
    return res || defaultValue;
  }
  try {
    data = yield res.json();
  } catch (e) {
    window.console.error('Error parse JSON', e?.message || '');
    data = defaultValue;
  }
  if (res?.status >= 400 && data && typeof data === 'object' && !Array.isArray(data)) {
    return { ...data, httpStatus: res.status };
  }
  return data;
}
